import { Theme, useTheme } from "@/components/theme/themeProvider"
import { Button } from "@/components/ui/baseUi/button"
import { DropdownInput } from "@/components/ui/forms/dropdownInput"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { useExerciseScoreFormActions } from "@/modules/scores/scoreForms/hooks/useExerciseScoreFormActions"
import { type ScoreResultDraftType, type ScoreType } from "@/modules/scores/scoreTypes"
import type { ExerciseFormViewEnum } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/components/exerciseForm"
import type {
  ExerciseFormikType,
  ExerciseInputKey,
} from "@/modules/sessions/sessionForm/blocks/exerciseBlock/exerciseFormikTypes"
import { createExerciseScoreFormModel } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/models/exerciseScoreFormModel"
import type { SimpleFormik } from "@/typings/formik"
import { InfoIconDark, InfoIconLight } from "@images/svgIcons/infoIcon"
import { word } from "@modules/core/utils/i18n"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { CircleXIcon, PlusIcon } from "lucide-react"

export function ExerciseScoreForm({
  formik,
  setCurrentView,
  className,
  ...props
}: {
  formik: SimpleFormik<ExerciseFormikType, ExerciseInputKey>
  setCurrentView: (view: ExerciseFormViewEnum) => void
} & React.ComponentProps<"div">) {
  const { theme } = useTheme()
  const confirmPopup = useConfirmPopup()

  const {
    scoreSetConfigurations,
    scoringOption,
    scoreType,
    addScoreSetRow,
    removeScoreSetRow,
    updateScoreSetRow,
    updateScoringOption,
  } = useExerciseScoreFormActions(formik.values.scoreSetConfigurations, formik.values.scoringOption)
  const model = createExerciseScoreFormModel(scoreSetConfigurations, scoringOption)

  const validateForm = async () => {
    // display a confirmation modal when an existing scoreSetConfiguration type has changed or was removed
    const existingScoreSetConfigurations = formik.values.scoreSetConfigurations.filter((scoreSet) => !!scoreSet.id)
    const updatedScoreSetConfigurations = scoreSetConfigurations.filter((scoreSet) => !!scoreSet.id)

    if (
      existingScoreSetConfigurations.some(
        (scoreSet) => !updatedScoreSetConfigurations.some((updatedScoreSet) => updatedScoreSet.id === scoreSet.id),
      ) ||
      existingScoreSetConfigurations.some(
        (scoreSet) =>
          scoreSet.scoreType !==
          updatedScoreSetConfigurations.find((updatedScoreSet) => updatedScoreSet.id === scoreSet.id)?.scoreType,
      )
    ) {
      const confirmationModaleContent = (
        <div className="mb-4 flex flex-col gap-8">
          <div className="flex flex-col items-center justify-center gap-2.5">
            {theme === Theme.OwnerDark ? (
              <InfoIconDark color="currentColor" className="text-red-500" />
            ) : (
              <InfoIconLight color="currentColor" className="text-red-500" />
            )}
            <Text fontStyle="TL" className="text-center text-textDefault-body">
              {word("sessionForm.exercise.scoreForm.modify.confirmation.title")}
            </Text>
          </div>

          <Text fontStyle="BM" className="text-center whitespace-pre-line text-textDefault-body">
            {word("sessionForm.exercise.scoreForm.modify.confirmation.message")}
          </Text>
        </div>
      )

      confirmPopup.show({
        title: "",
        message: confirmationModaleContent,
        accept: async () => {
          await handleEditScoreSetConfigurations()
        },
        classNames: {
          dialogContent: "w-128",
          dialogFooter: "w-full grow",
          dialogFooterConfirm: "flex-1",
          dialogFooterCancel: "flex-1",
        },
        footerProps: {
          cancelText: word("global.cancel"),
          confirmText: word("sessionForm.exercise.scoreForm.modify.confirmation.confirm"),
        },
      })
    } else {
      await handleEditScoreSetConfigurations()
    }
  }

  const handleEditScoreSetConfigurations = async () => {
    await formik.setFieldValue("scoreType", scoreType)
    await formik.setFieldValue("scoreSetConfigurations", scoreSetConfigurations)
    await formik.setFieldValue("scoringOption", scoringOption)
    setCurrentView("exerciseContent")
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      {/* Heading */}
      <div className="flex flex-col gap-2.5">
        <Text fontStyle="TL" className="font-semibold">
          {word("sessionForm.exercise.scoreForm.title")}
        </Text>
        <Text fontStyle="BS" className="text-neutral-600">
          {word("sessionForm.exercise.scoreForm.subtitle")}
        </Text>
      </div>

      {/* Score type input */}
      <div className="flex flex-col gap-2 rounded-2xl bg-surface-foreground px-5 py-6">
        <Text fontStyle="LS" className="text-textDefault-body uppercase">
          {word("sessionForm.exercise.scoreForm.scoreType.label")}
        </Text>

        <div className="flex flex-col gap-4">
          {scoreSetConfigurations.map((scoreSet, index) => (
            <ScoreSetRowInput
              key={index}
              index={index}
              scoreType={scoreSet.scoreType}
              onDeleteSetRow={() => {
                removeScoreSetRow(index)
              }}
              onScoreTypeChange={(value) => {
                updateScoreSetRow(index, value)
              }}
              canBeDeleted={model.canRemoveScoreSetRow}
              scoreTypeOptions={model.availableScoreTypes}
            />
          ))}

          <Button
            variant="outline"
            size="md"
            onClick={() => {
              addScoreSetRow()
            }}
            disabled={!model.canAddScoreSetRow}
          >
            <PlusIcon color="currentColor" />
            {word("sessionForm.exercise.scoreForm.scoreType.addScoreSetRow")}
          </Button>
        </div>
      </div>

      {/* Selected result input */}
      <div className="rounded-2xl bg-surface-foreground px-5 py-6">
        <ScoreResultTypeInput
          selectedResult={scoringOption}
          onSelectedResultChange={(value) => {
            updateScoringOption(value)
          }}
          disabled={!model.canSetScoreResultType}
          resultTypeOptions={model.availableScoreResultTypes}
        />
      </div>

      <Button
        variant="primary"
        className="w-full"
        size="lg"
        onClick={() => void validateForm()}
        disabled={!model.canValidateScoreForm}
      >
        {word("global.check")}
      </Button>
    </div>
  )
}

function ScoreSetRowInput({
  index,
  scoreType,
  onDeleteSetRow,
  onScoreTypeChange,
  className,
  canBeDeleted,
  scoreTypeOptions,
  ...props
}: {
  index: number
  scoreType: string
  onDeleteSetRow: () => void
  onScoreTypeChange: (value: ScoreType) => void
  canBeDeleted: boolean
  scoreTypeOptions: { label: string; value: ScoreType }[]
} & React.ComponentProps<"div">) {
  return (
    <div className={cn("flex flex-row items-center justify-between gap-4", className)} {...props}>
      <Text fontStyle="TM" className="text-textDefault-body">
        {index + 1}.
      </Text>

      <DropdownInput options={scoreTypeOptions} isRequiredInput={true}>
        <Select
          items={scoreTypeOptions}
          value={scoreType}
          onValueChange={(value) => {
            onScoreTypeChange(value as ScoreType)
          }}
        >
          <SelectTrigger id={"scoreType"} className="bg-surface-background">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {scoreTypeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </DropdownInput>

      <Button
        variant="ghost"
        size="iconLg"
        onClick={onDeleteSetRow}
        disabled={!canBeDeleted}
        className={cn({ hidden: !canBeDeleted })}
      >
        <CircleXIcon className="size-6 text-neutral-500" />
      </Button>
    </div>
  )
}

function ScoreResultTypeInput({
  selectedResult,
  onSelectedResultChange,
  disabled,
  className,
  resultTypeOptions,
  ...props
}: {
  selectedResult: ScoreResultDraftType | null
  onSelectedResultChange: (value: ScoreResultDraftType) => void
  disabled: boolean
  resultTypeOptions: { label: string; value: ScoreResultDraftType }[]
} & React.ComponentProps<"div">) {
  return (
    <div className={cn("flex flex-col gap-4", { "opacity-50": disabled }, className)} {...props}>
      <DropdownInput
        options={resultTypeOptions}
        isRequiredInput={true}
        label={word("sessionForm.exercise.scoreForm.selectedResult.label")}
      >
        <Select<ScoreResultDraftType>
          items={resultTypeOptions}
          defaultValue={selectedResult ?? undefined}
          onValueChange={(value) => {
            if (!value) return
            onSelectedResultChange(value)
          }}
          disabled={disabled}
        >
          <SelectTrigger id={"selectedResult"} className="bg-surface-background">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {resultTypeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </DropdownInput>
    </div>
  )
}
